// Standard MIDI file reading, key detection and in-place transposition.
// MIDI has no note spelling, so the key comes from a key signature event or is estimated from the notes.

import type {Mode, ScoreAnalysis} from "./musicxml";
import type {PlayNote, Timeline} from "./player";

interface MidiEvent {
    tick: number;
    track: number;
    // Channel status byte, 0xFF for meta, 0xF0/0xF7 for sysex
    status: number;
    metaType: number;
    // Index of the first data byte in the file
    dataStart: number;
    dataLength: number;
}

export interface MidiFile {
    bytes: Uint8Array;
    ppq: number;
    events: MidiEvent[];
    lastTick: number;
}

export interface MidiAnalysis extends ScoreAnalysis {
    keyFromFile: boolean;
}

const DRUM_CHANNEL = 9;
const DEFAULT_BPM = 120;

const mod = (n: number, m: number) => ((n % m) + m) % m;

export function isMidi(bytes: Uint8Array): boolean {
    return bytes[0] === 0x4d && bytes[1] === 0x54 && bytes[2] === 0x68 && bytes[3] === 0x64;
}

export function parseMidi(bytes: Uint8Array): MidiFile {
    const u32 = (p: number) => ((bytes[p] << 24) | (bytes[p + 1] << 16) | (bytes[p + 2] << 8) | bytes[p + 3]) >>> 0;
    const u16 = (p: number) => (bytes[p] << 8) | bytes[p + 1];
    const tag = (p: number) => String.fromCharCode(bytes[p], bytes[p + 1], bytes[p + 2], bytes[p + 3]);
    const vlq = (p: number): [number, number] => {
        let value = 0;
        for (let i = 0; i < 4; i++) {
            const b = bytes[p++];
            value = (value << 7) | (b & 0x7f);
            if (!(b & 0x80)) break;
        }
        return [value, p];
    };

    if (!isMidi(bytes)) throw new Error("Файл не похож на MIDI.");
    const division = u16(12);
    if (division & 0x8000) throw new Error("MIDI с SMPTE-временем не поддерживается.");

    const events: MidiEvent[] = [];
    let lastTick = 0;
    let pos = 8 + u32(4);
    let track = 0;

    while (pos + 8 <= bytes.length) {
        const size = u32(pos + 4);
        const start = pos + 8;
        const end = Math.min(start + size, bytes.length);
        if (tag(pos) === "MTrk") {
            let p = start;
            let tick = 0;
            let running = 0;
            while (p < end) {
                let delta;
                [delta, p] = vlq(p);
                tick += delta;
                let status = bytes[p];
                if (status >= 0x80) {
                    p++;
                    if (status < 0xf0) running = status;
                } else if (running) {
                    status = running;
                } else {
                    throw new Error("Повреждённый MIDI: данные без статуса.");
                }

                if (status === 0xff) {
                    const metaType = bytes[p++];
                    let length;
                    [length, p] = vlq(p);
                    events.push({tick, track, status, metaType, dataStart: p, dataLength: length});
                    p += length;
                    if (metaType === 0x2f) break;
                } else if (status === 0xf0 || status === 0xf7) {
                    let length;
                    [length, p] = vlq(p);
                    p += length;
                } else {
                    const length = (status & 0xf0) === 0xc0 || (status & 0xf0) === 0xd0 ? 1 : 2;
                    events.push({tick, track, status, metaType: 0, dataStart: p, dataLength: length});
                    p += length;
                }
            }
            lastTick = Math.max(lastTick, tick);
            track++;
        }
        pos = start + size;
    }

    // Stable sort keeps each track's order for events on the same tick
    events.sort((a, b) => a.tick - b.tick);
    return {bytes, ppq: division || 480, events, lastTick};
}

export interface MidiNote {
    // Ticks
    start: number;
    end: number;
    pitch: number;
    velocity: number;
    track: number;
    channel: number;
}

// Pairs note-ons with note-offs (first in, first out per channel and pitch); drums are left out
export function midiNotes(midi: MidiFile): MidiNote[] {
    const {bytes} = midi;
    const notes: MidiNote[] = [];
    const open = new Map<number, MidiNote[]>();
    for (const e of midi.events) {
        const type = e.status & 0xf0;
        const channel = e.status & 0x0f;
        if (e.status === 0xff || (type !== 0x80 && type !== 0x90) || channel === DRUM_CHANNEL) continue;
        const pitch = bytes[e.dataStart];
        const key = channel * 128 + pitch;
        if (type === 0x90 && bytes[e.dataStart + 1] > 0) {
            const note = {start: e.tick, end: e.tick, pitch, velocity: bytes[e.dataStart + 1], track: e.track, channel};
            notes.push(note);
            open.set(key, [...(open.get(key) ?? []), note]);
        } else {
            const note = open.get(key)?.shift();
            if (note) note.end = e.tick;
        }
    }
    open.forEach(list => list.forEach(n => (n.end = midi.lastTick)));
    return notes.sort((a, b) => a.start - b.start || b.pitch - a.pitch);
}

export function midiTempos(midi: MidiFile): Timeline["tempos"] {
    const {bytes, ppq} = midi;
    // Keep the last tempo set at each position
    const byPosition = new Map<number, number>();
    for (const e of midi.events) {
        if (e.status !== 0xff || e.metaType !== 0x51 || e.dataLength !== 3) continue;
        const micros = (bytes[e.dataStart] << 16) | (bytes[e.dataStart + 1] << 8) | bytes[e.dataStart + 2];
        if (micros > 0) byPosition.set(e.tick / ppq, 60_000_000 / micros);
    }
    const changes = Array.from(byPosition, ([at, bpm]) => ({at, bpm}));
    const initial = byPosition.get(0) ?? changes[0]?.bpm ?? DEFAULT_BPM;
    return [{at: 0, bpm: initial}, ...changes.filter(c => c.at > 0)];
}

export function midiTimeSignature(midi: MidiFile): { beats: number; beatType: number } {
    const e = midi.events.find(ev => ev.status === 0xff && ev.metaType === 0x58 && ev.dataLength >= 2);
    if (!e) return {beats: 4, beatType: 4};
    return {beats: midi.bytes[e.dataStart] || 4, beatType: 2 ** midi.bytes[e.dataStart + 1] || 4};
}

export interface PedalEvent {
    // Quarter notes
    at: number;
    down: boolean;
}

// Sustain pedal (CC64) presses and releases, in order, without repeated states
export function midiPedals(midi: MidiFile): PedalEvent[] {
    const events: PedalEvent[] = [];
    for (const e of midi.events) {
        if ((e.status & 0xf0) !== 0xb0 || (e.status & 0x0f) === DRUM_CHANNEL) continue;
        if (midi.bytes[e.dataStart] !== 64) continue;
        const down = midi.bytes[e.dataStart + 1] >= 64;
        if (events.length ? events[events.length - 1].down !== down : down) events.push({at: e.tick / midi.ppq, down});
    }
    return events;
}

// A note released while the pedal is down keeps sounding until the pedal comes up
function sustain(notes: PlayNote[], pedals: PedalEvent[]) {
    const nextStart = new Map<PlayNote, number>();
    const byPitch = new Map<number, PlayNote[]>();
    notes.forEach(n => byPitch.set(n.midi, [...(byPitch.get(n.midi) ?? []), n]));
    byPitch.forEach(list => list.forEach((n, i) => nextStart.set(n, list[i + 1]?.start ?? Infinity)));
    for (const n of notes) {
        const pressed = pedals.filter(p => p.at <= n.end + 1e-6).pop();
        if (!pressed?.down) continue;
        const release = pedals.find(p => p.at > n.end + 1e-6 && !p.down);
        if (release) n.end = Math.min(release.at, nextStart.get(n)!);
    }
}

export function midiTimeline(midi: MidiFile): Timeline {
    const notes: PlayNote[] = midiNotes(midi).map(n => ({
        start: n.start / midi.ppq, end: n.end / midi.ppq, midi: n.pitch, velocity: n.velocity,
    }));
    sustain(notes, midiPedals(midi));
    return {
        notes: notes.sort((a, b) => a.start - b.start),
        tempos: midiTempos(midi),
        length: midi.lastTick / midi.ppq,
    };
}

function keySignature(midi: MidiFile): { fifths: number; mode: Mode } | null {
    const e = midi.events.find(ev => ev.status === 0xff && ev.metaType === 0x59 && ev.dataLength === 2);
    if (!e) return null;
    const sf = midi.bytes[e.dataStart];
    return {fifths: sf > 127 ? sf - 256 : sf, mode: midi.bytes[e.dataStart + 1] === 1 ? "minor" : "major"};
}

// Krumhansl–Schmuckler key profiles
const MAJOR_PROFILE = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const MINOR_PROFILE = [6.33, 2.68, 3.52, 5.38, 2.6, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

function correlation(a: number[], b: number[]): number {
    const mean = (v: number[]) => v.reduce((s, x) => s + x, 0) / v.length;
    const ma = mean(a);
    const mb = mean(b);
    let num = 0;
    let da = 0;
    let db = 0;
    a.forEach((x, i) => {
        num += (x - ma) * (b[i] - mb);
        da += (x - ma) ** 2;
        db += (b[i] - mb) ** 2;
    });
    return da && db ? num / Math.sqrt(da * db) : 0;
}

// Fifths of the major key whose tonic is this pitch class, spelled with at most 6 flats or 5 sharps
const fifthsOfMajorTonic = (pc: number) => mod(7 * pc + 6, 12) - 6;

export function estimateKey(durations: number[]): { fifths: number; mode: Mode } {
    let best = {fifths: 0, mode: "major" as Mode, score: -Infinity};
    for (let tonic = 0; tonic < 12; tonic++) {
        const rotated = durations.map((_, i) => durations[mod(i + tonic, 12)]);
        const major = correlation(rotated, MAJOR_PROFILE);
        const minor = correlation(rotated, MINOR_PROFILE);
        if (major > best.score) best = {fifths: fifthsOfMajorTonic(tonic), mode: "major", score: major};
        if (minor > best.score) best = {fifths: fifthsOfMajorTonic(tonic + 3), mode: "minor", score: minor};
    }
    return {fifths: best.fifths, mode: best.mode};
}

export function analyzeMidi(midi: MidiFile, timeline: Timeline): MidiAnalysis {
    const counts = new Array(12).fill(0);
    const durations = new Array(12).fill(0);
    let minMidi = Infinity;
    let maxMidi = -Infinity;
    for (const n of timeline.notes) {
        counts[n.midi % 12]++;
        durations[n.midi % 12] += Math.max(n.end - n.start, 0.05);
        minMidi = Math.min(minMidi, n.midi);
        maxMidi = Math.max(maxMidi, n.midi);
    }

    const estimated = estimateKey(durations);
    const signature = keySignature(midi);
    // Many editors write C major by default, so a bare C/Am signature only counts when the notes agree
    const trusted = signature && (signature.fifths !== 0 || estimated.fifths === 0) ? signature : null;
    const key = trusted ?? estimated;

    return {
        fifths: key.fifths,
        mode: key.mode,
        pitchClassCounts: counts,
        noteCount: timeline.notes.length,
        minMidi,
        maxMidi,
        keyFromFile: !!trusted,
    };
}

export function transposeTimeline(timeline: Timeline, semitones: number): Timeline {
    return {...timeline, notes: timeline.notes.map(n => ({...n, midi: n.midi + semitones}))};
}

// Returns a copy with notes (except drums) and key signatures moved; byte layout stays the same
export function transposeMidi(midi: MidiFile, fifthsDelta: number, semitones: number): Uint8Array {
    const out = midi.bytes.slice();
    for (const e of midi.events) {
        if (e.status === 0xff) {
            if (e.metaType === 0x59 && e.dataLength === 2) {
                const sf = out[e.dataStart] > 127 ? out[e.dataStart] - 256 : out[e.dataStart];
                let fifths = sf + fifthsDelta;
                if (fifths > 6) fifths -= 12;
                if (fifths < -6) fifths += 12;
                out[e.dataStart] = mod(fifths, 256);
            }
            continue;
        }
        const type = e.status & 0xf0;
        if ((type === 0x80 || type === 0x90 || type === 0xa0) && (e.status & 0x0f) !== DRUM_CHANNEL) {
            out[e.dataStart] = Math.min(127, Math.max(0, out[e.dataStart] + semitones));
        }
    }
    return out;
}
